import React from 'react';
import { Box, Link, Typography } from '@mui/material';

interface MarkdownTextProps {
  content: string;
  fontSize?: string;
}

// Lightweight markdown renderer for chat messages: headings, lists, code blocks,
// bold/italic/inline code, and links. Renders React elements only — no raw HTML —
// so model output cannot inject markup.

const INLINE_PATTERN = /(\*\*[^*]+\*\*|\*[^*\n]+\*|`[^`\n]+`|\[[^\]\n]+\]\((?:https?:\/\/)[^)\s]+\))/g;

const renderInline = (text: string, keyPrefix: string): React.ReactNode[] => {
  const parts = text.split(INLINE_PATTERN);
  return parts.filter(Boolean).map((part, i) => {
    const key = `${keyPrefix}-${i}`;
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={key}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <em key={key}>{part.slice(1, -1)}</em>;
    }
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return (
        <Box
          key={key}
          component="code"
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.85em',
            px: 0.5,
            py: 0.1,
            borderRadius: 0.5,
            bgcolor: 'action.hover',
          }}
        >
          {part.slice(1, -1)}
        </Box>
      );
    }
    const linkMatch = part.match(/^\[([^\]]+)\]\(((?:https?:\/\/)[^)\s]+)\)$/);
    if (linkMatch) {
      return (
        <Link key={key} href={linkMatch[2]} target="_blank" rel="noopener noreferrer">
          {linkMatch[1]}
        </Link>
      );
    }
    return <React.Fragment key={key}>{part}</React.Fragment>;
  });
};

type Block =
  | { type: 'paragraph'; lines: string[] }
  | { type: 'heading'; level: number; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] }
  | { type: 'code'; text: string };

const parseBlocks = (content: string): Block[] => {
  const lines = content.split('\n');
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim().startsWith('```')) {
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // skip closing fence
      blocks.push({ type: 'code', text: codeLines.join('\n') });
      continue;
    }

    const headingMatch = line.match(/^(#{1,4})\s+(.*)$/);
    if (headingMatch) {
      blocks.push({ type: 'heading', level: headingMatch[1].length, text: headingMatch[2] });
      i++;
      continue;
    }

    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i++;
      }
      blocks.push({ type: 'ul', items });
      continue;
    }

    if (/^\s*\d+[.)]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+[.)]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+[.)]\s+/, ''));
        i++;
      }
      blocks.push({ type: 'ol', items });
      continue;
    }

    if (line.trim() === '') {
      i++;
      continue;
    }

    const paragraphLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].trim().startsWith('```') &&
      !/^(#{1,4})\s+/.test(lines[i]) &&
      !/^\s*[-*]\s+/.test(lines[i]) &&
      !/^\s*\d+[.)]\s+/.test(lines[i])
    ) {
      paragraphLines.push(lines[i]);
      i++;
    }
    blocks.push({ type: 'paragraph', lines: paragraphLines });
  }

  return blocks;
};

const MarkdownText: React.FC<MarkdownTextProps> = ({ content, fontSize = '0.9rem' }) => {
  const blocks = parseBlocks(content);

  return (
    <Box sx={{ fontSize, wordBreak: 'break-word', '& > *:first-of-type': { mt: 0 }, '& > *:last-child': { mb: 0 } }}>
      {blocks.map((block, bi) => {
        switch (block.type) {
          case 'heading':
            return (
              <Typography
                key={bi}
                component="div"
                sx={{
                  fontSize: block.level <= 2 ? '1.05em' : '1em',
                  fontWeight: 700,
                  mt: 1.25,
                  mb: 0.5,
                }}
              >
                {renderInline(block.text, `h${bi}`)}
              </Typography>
            );
          case 'code':
            return (
              <Box
                key={bi}
                component="pre"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.85em',
                  p: 1,
                  my: 1,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  overflowX: 'auto',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {block.text}
              </Box>
            );
          case 'ul':
          case 'ol':
            return (
              <Box key={bi} component={block.type} sx={{ my: 0.5, pl: 2.5, '& li': { mb: 0.25 } }}>
                {block.items.map((item, ii) => (
                  <li key={ii}>{renderInline(item, `${bi}-${ii}`)}</li>
                ))}
              </Box>
            );
          case 'paragraph':
          default:
            return (
              <Typography key={bi} component="div" sx={{ fontSize: 'inherit', my: 0.5 }}>
                {block.lines.map((l, li) => (
                  <React.Fragment key={li}>
                    {li > 0 && <br />}
                    {renderInline(l, `${bi}-${li}`)}
                  </React.Fragment>
                ))}
              </Typography>
            );
        }
      })}
    </Box>
  );
};

export default MarkdownText;
